import { LocaleCode, SUPPORTED_LOCALES } from "../schemas/locale.schemas.js";
import type { LocalizedText } from "../schemas/localized-text.schemas.js";

export const LocalizedTextUtils = {
  /**
   * Returns the translation for `locale` when present; otherwise falls back
   * to the canonical `content`, which may be in a different locale. LLM-bound
   * call sites pass `DEFAULT_LOCALE` — a Spanish-only entry still surfaces
   * its Spanish content (LLMs accept cross-locale input).
   */
  resolve(localizedText: LocalizedText, locale: string): string {
    if (
      localizedText.translations !== undefined &&
      locale in localizedText.translations
    ) {
      const translation = localizedText.translations[locale];
      if (translation !== undefined) {
        return translation;
      }
    }

    return localizedText.content;
  },

  /**
   * Resolves to a non-empty (trimmed) string, preferring `locale`, then the
   * canonical `content`, then any other translation — so a value present in ANY
   * locale is surfaced. Returns null only when every candidate is empty or
   * whitespace. Unlike `resolve`, which returns the canonical `content` even
   * when it is empty and a non-empty translation exists in another locale.
   */
  resolveNonEmpty(localizedText: LocalizedText, locale: string): string | null {
    const candidates: readonly string[] = [
      localizedText.translations?.[locale] ?? "",
      localizedText.content,
      ...Object.values(localizedText.translations ?? {}),
    ];
    for (const candidate of candidates) {
      const trimmed = candidate.trim();
      if (trimmed.length > 0) {
        return trimmed;
      }
    }
    return null;
  },

  resolveBulletsToText(
    bullets: readonly LocalizedText[],
    locale: string,
    separator: string,
  ): string {
    return bullets.map((b) => this.resolve(b, locale)).join(separator);
  },

  fromString(content: string, lang?: LocaleCode): LocalizedText {
    return {
      lang: lang ?? LocaleCode.NOT_SET,
      content,
      translations: {},
    };
  },

  /**
   * Promotes `locale` to canonical and demotes the previous canonical into
   * `translations`. Returns the input unchanged when `locale` already matches
   * canonical or is absent from `translations`.
   */
  recanonicalize(
    localizedText: LocalizedText,
    locale: LocaleCode,
  ): LocalizedText {
    if (localizedText.lang === locale) {
      return localizedText;
    }
    const translations = localizedText.translations ?? {};
    const promoted = translations[locale];
    if (promoted === undefined) {
      return localizedText;
    }
    const nextTranslations: Partial<Record<LocaleCode, string>> = {
      ...translations,
    };
    delete nextTranslations[locale];
    nextTranslations[localizedText.lang] = localizedText.content;
    return {
      lang: locale,
      content: promoted,
      translations: nextTranslations,
    };
  },

  // Empty strings in non-primary locales are dropped so resolve() falls back to content.
  fromRecord(
    record: Readonly<Partial<Record<LocaleCode, string>>>,
    primaryLocale: LocaleCode,
  ): LocalizedText | null {
    const primary = record[primaryLocale];
    if (primary === undefined || primary === "") {
      return null;
    }

    const translations: Partial<Record<LocaleCode, string>> = {};
    for (const locale of SUPPORTED_LOCALES) {
      if (locale === primaryLocale) {
        continue;
      }
      const value = record[locale];
      if (value !== undefined && value !== "") {
        translations[locale] = value;
      }
    }

    return {
      lang: primaryLocale,
      content: primary,
      translations,
    };
  },
};
